import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { AssetImg } from 'javascripts/components/asset_img';
import { AudiobookCreditNames } from 'javascripts/components/audiobook/audiobook_credit_names';

import { breakpoints } from 'javascripts/shared/utils/breakpoint_utils';
import { useDimensions } from 'javascripts/hooks/use-dimensions';
import { useMediaQuery } from 'react-responsive';

import formatIcon from 'images/Audio.png';
import narratorIcon from 'images/Speak.png';
import authorIcon from 'images/Quill.png';
import runTimeIcon from 'images/Time.png';

import styles from 'stylesheets/components/audiobook/audiobook_detail_grid.module';

export function AudiobookDetailGrid({ authors, narrators, runtime, dark }) {
  // matches breakpoint used in audiobook_app stylesheet
  const isMobile = useMediaQuery({ maxWidth: breakpoints.lg });

  const [authorCellRef, { y: authorY }] = useDimensions(true, ['resize']);
  const [narratorCellRef, { y: narratorY }] = useDimensions(true, ['resize']);
  const [formatCellRef, { y: formatY }] = useDimensions(true, ['resize']);
  const [showAuthorCellSpacer, setShowAuthorCellSpacer] = useState(true);
  const [showFormatCellSpacer, setShowFormatCellSpacer] = useState(!isMobile);

  useLayoutEffect(() => {
    if (!isMobile) {
      setShowAuthorCellSpacer(true);
    } else if (authorY && narratorY) {
      setShowAuthorCellSpacer(authorY.toFixed(1) === narratorY.toFixed(1));
    }
  }, [authorY, narratorY, isMobile]);

  useLayoutEffect(() => {
    if (isMobile) {
      setShowFormatCellSpacer(false);
    } else if (narratorY && formatY) {
      setShowFormatCellSpacer(narratorY.toFixed(1) === formatY.toFixed(1));
    }
  }, [narratorY, formatY, isMobile]);

  function renderDetailCell(icon, heading, content, ref = null) {
    return (
      <div className={styles.cell} ref={ref}>
        <AssetImg src={icon} attrs={{ alt: '', className: styles.icon }} />
        <div className={styles.content}>
          <div className={styles.heading}>{heading}</div>
          {content}
        </div>
      </div>
    );
  }

  function renderSpacer() {
    return (
      <div className={styles.spacerWrapper}>
        <div className={styles.spacer}></div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.detailGrid, { [styles.dark]: dark })} data-testid='audiobook-detail-grid'>
      <div className={styles.authorNarratorSection}>
        {renderDetailCell(
          authorIcon,
          'Written by',
          <AudiobookCreditNames credits={authors} type='author' />,
          authorCellRef,
        )}
        {showAuthorCellSpacer ? renderSpacer() : null}
        {renderDetailCell(
          narratorIcon,
          'Read by',
          <AudiobookCreditNames credits={narrators} type='narrator' />,
          narratorCellRef,
        )}
      </div>
      {showFormatCellSpacer ? renderSpacer() : null}
      <div className={styles.formatRunTimeSection}>
        {renderDetailCell(formatIcon, 'Format', 'Digital Audiobook', formatCellRef)}
        {renderSpacer()}
        {renderDetailCell(runTimeIcon, 'Run Time', runtime)}
      </div>
    </div>
  );
}

AudiobookDetailGrid.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  narrators: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
  runtime: PropTypes.string.isRequired,
  dark: PropTypes.bool,
};

AudiobookDetailGrid.defaultProps = {
  dark: false,
};
